import {
  type CSSProperties,
  type FC,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import cx from 'classnames';
import type { AssistantMarkdownImage } from '../../types/assistant';
// Фото в карточке занимает clamp(112px, 30%, 280px) — что близко к трети
// ширины чата. Браузер выберет ближайший подходящий вариант из srcSet.
const PHOTO_SIZES = '(min-width: 933px) 280px, 30vw';
import { ResponsiveImage } from '../ResponsiveImage';
import { UnstyledButton } from '../UnstyledButton';

import styles from './SceneBannerCard.module.css';

type SceneBannerCardProps = {
  image: AssistantMarkdownImage;
  buttonLabel: string;
  buttonIcon: ReactNode;
  onEnterScene: (sceneId: string) => void;
  /**
   * Compact mode for use inside a horizontal slider (`SceneBannerSlider`):
   * fixed photo width 112px, fixed min-height 120px, no outer margin
   * (the slider owns the gap between cards).
   */
  compact?: boolean;
  /**
   * If `true`, the whole card becomes the click target (the inner button
   * stays as a visual cue, no longer interactive). Used by chat banners.
   * If `false` (default), only the button is clickable — used by
   * `OverallSceneOverlay`, where the cinematic fly-in fires from the button
   * and the rest of the card must stay passive for swipe interactions.
   */
  cardClickable?: boolean;
  className?: string;
  style?: CSSProperties;
};

export const SceneBannerCard: FC<SceneBannerCardProps> = ({
  image,
  buttonLabel,
  buttonIcon,
  onEnterScene,
  compact,
  cardClickable,
  className,
  style,
}) => {
  const sceneId = image.moveToSceneId ?? null;

  const handleClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (sceneId) onEnterScene(sceneId);
  };

  const content = (
    <>
      <div className={styles.content}>
        <div className={styles.text}>
          {image.title && (
            <h3 className={styles.title} dir="auto">
              {image.title}
            </h3>
          )}
          {image.description && (
            <p className={styles.description} dir="auto">
              {image.description}
            </p>
          )}
        </div>
        {cardClickable ? (
          <span
            className={cx(styles.button, styles.buttonStatic)}
            aria-hidden="true"
          >
            <span className={styles.buttonIcon}>{buttonIcon}</span>
            <span>{buttonLabel}</span>
          </span>
        ) : (
          <UnstyledButton
            className={styles.button}
            onClick={handleClick}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={buttonLabel}
          >
            <span className={styles.buttonIcon}>{buttonIcon}</span>
            <span>{buttonLabel}</span>
          </UnstyledButton>
        )}
      </div>
      <div className={styles.photoWrap}>
        <ResponsiveImage
          className={styles.photo}
          src={image.src}
          sizes={PHOTO_SIZES}
          originalWidth={image.width}
          width={image.width}
          height={image.height}
          alt={image.title ?? undefined}
        />
      </div>
    </>
  );

  if (cardClickable && sceneId) {
    return (
      <UnstyledButton
        className={cx(styles.card, styles.cardClickable, compact && styles.compact, className)}
        style={style}
        onClick={() => onEnterScene(sceneId)}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={buttonLabel}
      >
        {content}
      </UnstyledButton>
    );
  }

  return (
    <figure
      className={cx(styles.card, compact && styles.compact, className)}
      style={style}
    >
      {content}
    </figure>
  );
};
